import React, { memo, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import * as mapboxgl from 'mapbox-gl'
import * as MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import { distance } from '@turf/turf'
import { returnMarkupTX } from './mappopuptx'
import { getBbox } from './mapbounding'

// styles
import mapboxStyles from '../../../styles/modules/mapboxmain.module.less'
import '../../../styles/mapboxpopup.less'
import '../../../styles/mapboxgeocoder.less'
// import '../../../../node_modules/mapbox-gl/dist/mapbox-gl.css'
// import '../../../../node_modules/@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'

const MapboxTX = ({
  mapData,
  center,
  zoom,
  mapStyle = 'mapbox://styles/mapbox/light-v10',
}) => {
  // grab loading spinner
  const loader = useRef(null)
  const [showModal, setShowModal] = useState(null)
  //setShowModal({Name: namevariable})

  // make a copy of the mapData so we don't accidentally mutate the data coming into the component
  const mapDataCopy = JSON.parse(JSON.stringify(mapData))

  // useEffect(() => {

  // },[])

  useEffect(() => {
    // used for markers and to prevent mutating prop data when features sorted later. in the shape of a geojson

    const testSites = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: mapDataCopy.map((feature, index) => {
          let newFeature = {}
          Object.assign(newFeature, feature)
          newFeature.properties.id = index
          return newFeature
        }),
      },
    }

    mapboxgl.accessToken =
      'pk.eyJ1IjoiZGV2aHViIiwiYSI6ImNrbGd3eW1hdjU2ZjAycHVpaTQ4cGVybHUifQ.8vvwkoGVRI2riKkei6t-FQ'

    var bounds = [
      [-111.4697816, 24.8169964], // Southwest coordinates
      [-91.694391, 38.9682448], // Northeast coordinates
    ]

    // set-up map
    const map = new mapboxgl.Map({
      container: 'map',
      style: mapStyle,
      zoom: zoom,
      center: center,
      maxBounds: bounds, // Sets bounds as max
    })

    // set up address lookup
    const geocoder = new MapboxGeocoder({
      accessToken:
        'pk.eyJ1IjoiZGV2aHViIiwiYSI6ImNrbGd3eW1hdjU2ZjAycHVpaTQ4cGVybHUifQ.8vvwkoGVRI2riKkei6t-FQ',
      mapboxgl: mapboxgl,
      countries: 'us',
      marker: {
        color: '#3f0676',
      },
    })

    // set up geolocator
    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      fitBoundsOptions: {
        maxZoom: 12,
      },
      trackUserLocation: true,
    })

    // set up zoom buttons
    const nav = new mapboxgl.NavigationControl({
      showZoom: true,
      showCompass: false,
    })

    // set up info popups
    const popup = new mapboxgl.Popup()

    map.on('load', () => {
      // add geojson we created to map as data source and then attach as circle layer
      map.addSource('testing-sites', testSites)
      map.addLayer({
        id: 'test-sites',
        type: 'circle',
        source: 'testing-sites',
        paint: {
          'circle-color': '#3f0676',
          'circle-opacity': 0.3,
          'circle-stroke-color': '#3f0776',
          'circle-stroke-width': 1,
          'circle-stroke-opacity': 0.4,
          'circle-radius': 4.5,
        },
      })
    })

    // add click handler for each circle
    map.on('click', 'test-sites', (event) => {
      // when over the feature turn cursor into pointer
      map.getCanvas().style.cursor = 'pointer'
      const {
        features: [
          {
            properties: {
              Name,
              Address,
              Website,
              Phone,
              Moderna,
              Pfizer,
              UpdateTime,
              UpdateDate,
            },
          },
        ],
      } = event

      // Grab location of feature
      const coordinates = event.features[0].geometry.coordinates.slice()
      // Ensure that if the map is zoomed out such that multiple copies of the feature are visible, the popup appears over the copy being pointed to.
      while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360
      }

      // if someone searched address lower, we want to find distance and include in popup so we match og against search mutated data to find appropriate value
      let siteDistance
      if (Address === null || Address === 'null') {
        siteDistance = undefined
      } else {
        siteDistance = testSites.data.features.find(
          (feature) => feature.properties.Address === Address
        ).properties.distance
      }

      // display popup with info we want
      popup
        .setLngLat(coordinates)
        .setHTML(
          returnMarkupTX({
            Name,
            Address,
            Website,
            Phone,
            Moderna,
            Pfizer,
            UpdateTime,
            UpdateDate,
          })
        )
        .addTo(map)

      // let showmore = document.getElementById("showmore")
      // showmore.addEventListener("click", ()=>{setShowModal({
      //   Appointment_scheduling_instructions,
      //   Latest_report_notes
      // })})

      // track circle selects
      if (typeof window.ens_specialEvent !== 'undefined') {
        window.ens_specialEvent(
          'COVID-19 Testing Sites Map',
          'Select',
          'Info Circle'
        )
      }
    })

    // when over the feature turn cursor into pointer
    map.on('mouseenter', 'test-sites', () => {
      map.getCanvas().style.cursor = 'pointer'
    })

    // when not over the feature set default cursor
    map.on('mouseleave', 'test-sites', () => {
      map.getCanvas().style.cursor = ''
    })

    // set up geocoder input returns
    geocoder.on('result', (event) => {
      // grab location from search
      const {
        result: { geometry: searchResult },
      } = event
      // get all test sites
      const {
        data: { features: sites },
      } = testSites
      // set up turf options
      const options = { units: 'miles' }
      // find the distance between the search result and each site and attach to each site object as property
      sites.forEach((site) => {
        if (site.geometry !== null) {
          Object.defineProperty(site.properties, 'distance', {
            value: distance(searchResult, site.geometry, options),
            writable: true,
            enumerable: true,
            configurable: true,
          })
        } else {
          // console.log("geom", site)
        }
      })

      // sort sites by the newly calculated distance
      sites.sort((a, b) => {
        if (a.properties.distance > b.properties.distance) {
          return 1
        }
        if (a.properties.distance < b.properties.distance) {
          return -1
        }
        return 0
      })
      // create bounding box for top sorted (closest) result and zoom in there nicely
      const bbox = getBbox(testSites.data, 0, searchResult)
      map.fitBounds(bbox, {
        padding: 100,
      })
      // show popup for closest item
      const [
        {
          geometry: { coordinates },
          properties: {
            Name,
            Address,
            Website,
            Phone,
            Moderna,
            Pfizer,
            UpdateTime,
            UpdateDate,
          },
        },
      ] = sites
      popup
        .setLngLat(coordinates)
        .setHTML(
          returnMarkupTX({
            Name,
            Address,
            Website,
            Phone,
            Moderna,
            Pfizer,
            UpdateTime,
            UpdateDate,
          })
        )
        .addTo(map)

      // track address lookups
      if (typeof window.ens_specialEvent !== 'undefined') {
        window.ens_specialEvent(
          'COVID-19 Testing Sites Map',
          'Search',
          'Address lookup'
        )
      }
    })

    // set up geolocation returns
    geolocate.on('geolocate', (event) => {
      // make location object from event that turf will like
      const locateResult = {
        geometry: {
          coordinates: [event.coords.longitude, event.coords.latitude],
          type: 'Point',
        },
      }
      // get all test sites
      const {
        data: { features: sites },
      } = testSites
      // set up turf options
      const options = { units: 'miles' }
      // find the distance between the locate result and each site and attach to each site object as property
      sites.forEach((site) => {
        Object.defineProperty(site.properties, 'distance', {
          value: distance(locateResult, site.geometry, options),
          writable: true,
          enumerable: true,
          configurable: true,
        })
      })
    })

    geolocate.on('trackuserlocationstart', () => {
      // track geolocate triggers
      if (typeof window.ens_specialEvent !== 'undefined') {
        window.ens_specialEvent(
          'COVID-19 Testing Sites Map',
          'Select',
          'Geolocate'
        )
      }
    })

    // add locator, address lookup, and zoom controls to map

    map.addControl(geocoder, 'top-left')
    map.addControl(geolocate, 'top-right')
    map.addControl(nav, 'bottom-right')
    map.scrollZoom.disable()
    map.touchZoomRotate.enable()
  }, [])

  return (
    <figure
      className={mapboxStyles.mapbox}
      aria-label="Map showing testing sites in the Bay area that don’t require a referral"
    >
      <div className={mapboxStyles.map} id="map"></div>

      {/*

      {showModal &&
        <div className={mapboxStyles.modal}>
          { JSON.stringify(showModal) }
        </div>
      }

    */}
    </figure>
  )
}

MapboxTX.propTypes = {
  mapData: PropTypes.array.isRequired,
  center: PropTypes.array.isRequired,
  zoom: PropTypes.number.isRequired,
  mapStyle: PropTypes.string,
}

export default memo(MapboxTX)
